import React, { useEffect, useState, useRef, useCallback } from 'react';
import ReactModal from 'react-modal';
import { IoIosClose } from 'react-icons/io';
import Button from 'components/ui/button';
import { useModal } from 'context/ModalContext';
import { useAuth } from 'context/AuthContext';
import { registerInteractionCampaign } from 'services/actionlog.service';
import { getAnswersQuiz } from 'services/quest.service';
import { UserAvatar } from 'components/user-avatar';
import gsap from 'gsap';
import domtoimage from 'dom-to-image';

// Importar todas as imagens de cores
import cardColor1 from 'assets/img/ano-novo/card_final_de_ano_1.png';
import cardColor2 from 'assets/img/ano-novo/card_final_de_ano_2.png';
import cardColor3 from 'assets/img/ano-novo/card_final_de_ano_3.png';
import cardColor4 from 'assets/img/ano-novo/card_final_de_ano_4.png';
import cardColor5 from 'assets/img/ano-novo/card_final_de_ano_5.png';
import lacreEnvelope from 'assets/img/ano-novo/lacre-envelope.png';

import './style.scss';

interface NewYearEnvelopeModalProps {
  onClose?: () => void;
}

// Estrutura de uma pergunta do quiz
interface QuizQuestion {
  question: string;
  answer: string[];
  label: string;
  question_text: string;
  time: number;
}

// Estrutura da resposta da API
interface QuizResponse {
  player: string;
  quiz: string;
  quiz_log: string;
  questions: QuizQuestion[];
  message?: string; // Mensagem de reconhecimento (opcional)
}

interface UserData {
  cor: string;
  mood: string;
  superpoder: string;
  trilhaSonora: string;
  formaRecarregar: string;
  viagem: string;
  pequenaAlegria: string;
  atividadeFisica: string;
  simbolo: string;
  emoji: string;
  reconhecimento: string;
}

// Mapeamento de cor (answer) para imagem
const colorToImage: Record<string, string> = {
  '1': cardColor1,
  '2': cardColor2,
  '3': cardColor3,
  '4': cardColor4,
  '5': cardColor5,
};

// Extrai o texto do label removendo o emoji inicial
function extractLabelText(label: string): string {
  // Remove emoji e texto antes dos dois pontos se houver
  const colonIndex = label.indexOf(':');
  if (colonIndex !== -1) {
    return label.substring(colonIndex + 1).trim();
  }
  // Se não tiver dois pontos, remove caracteres não-letra do início
  return label.replace(/^[^\w\sÀ-ÿ]+\s*/g, '').trim();
}

// Extrai o texto do mood (remove emoji do início e ponto final)
function extractMoodText(label: string): string {
  let text = label;
  text = text.replace(/^[^\w\sÀ-ÿ]+\s*/g, '').trim();
  if (text.length > 5) {
    const start = text.slice(0, -5);
    const end = text.slice(-5).replace(/\./g, '');
    text = start + end;
  } else {
    text = text.replace(/\./g, '');
  }
  return text.trim();
}

// Palavras-chave para destacar no texto de reconhecimento
const HIGHLIGHT_KEYWORDS = [
  'liderança',
  'relacionamento interpessoal',
  'habilidades excepcionais',
  'desempenho',
  'colaborativo',
  'inclusivo',
  'inovadores',
  'eficientes',
  'proximidade',
  'relevância',
  'equipes',
  'resultados',
  'comprometimento',
  'competência técnica',
  'foco no cliente',
  'proatividade',
  'dedicação',
  'inovação',
  'agilidade',
  'empatia',
  'protagonismo',
  'visão estratégica',
  'conhecimento técnico',
  'excelência',
  'engajamento',
  'ética',
  'criatividade',
  'qualidade',
  'resolução de problemas',
  'aprendizado contínuo',
  'adaptação',
  'organização',
  'responsabilidade',
  'resiliência',
  'segurança',
  'transparência',
  'parceria',
  'assertividade',
  'melhoria contínua',
  'confiança',
  'habilidade técnica',
  'disponibilidade',
];

// Destaca palavras-chave no texto de reconhecimento
function highlightRecognitionText(text: string, color: string): React.ReactNode {
  if (!text) return null;

  // Cria regex para encontrar as palavras-chave (case insensitive)
  const pattern = HIGHLIGHT_KEYWORDS.join('|');
  const regex = new RegExp(`(${pattern})`, 'gi');

  // Divide o texto pelas palavras-chave
  const parts = text.split(regex);

  return parts.map((part, index) => {
    // Verifica se a parte é uma palavra-chave
    const isKeyword = HIGHLIGHT_KEYWORDS.some(keyword => part.toLowerCase() === keyword.toLowerCase());

    if (isKeyword) {
      return (
        <strong key={index} style={{ color }}>
          {part}
        </strong>
      );
    }
    return part;
  });
}

// IDs de modal suportados
const MODAL_ID_ENVELOPE = 'new-year-envelope';
const MODAL_ID_DIRECT = 'new-year-envelope-direct';

export function NewYearEnvelopeModal({ onClose }: NewYearEnvelopeModalProps) {
  const [modalIsOpen, setIsOpen] = useState(false);
  const [isDirectMode, setIsDirectMode] = useState(false);
  const [animationPhase, setAnimationPhase] = useState<'initial' | 'opening' | 'expanded'>('initial');
  const [fireworks, setFireworks] = useState<Array<{ id: number; x: number; y: number; color: string; delay: number }>>([]);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [cardImage, setCardImage] = useState<string>(cardColor3); // Default roxo
  const [isLoading, setIsLoading] = useState(true);
  const [hasQuizAnswers, setHasQuizAnswers] = useState(true); // Se o usuário respondeu o quiz

  const envelopeRef = useRef<HTMLDivElement>(null);
  const letterRef = useRef<HTMLDivElement>(null);
  const expandedCardRef = useRef<HTMLDivElement>(null);
  const focusTargetRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);

  const { openModal, closeModal } = useModal();
  const { user, userInfo } = useAuth();

  // Estado para mensagem dinâmica do aria-live
  const [liveMessage, setLiveMessage] = useState('');
  const lastAnnouncedMessage = useRef<string>('');

  ReactModal.setAppElement('#modal');

  // Função para anunciar mensagem apenas se for diferente da última
  const announceMessage = useCallback((message: string) => {
    if (message && message !== lastAnnouncedMessage.current) {
      lastAnnouncedMessage.current = message;
      setLiveMessage(message);
    }
  }, []);

  const handleCloseModal = useCallback(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    // Reseta mensagem para poder anunciar novamente ao reabrir
    lastAnnouncedMessage.current = '';
    setLiveMessage('');
    closeModal();
    onClose?.();
  }, [closeModal, onClose]);

  function afterOpenModal() {
    sendActionLog();
    generateFireworks();
    previouslyFocusedElement.current = document.activeElement as HTMLElement;
    setTimeout(() => {
      // Prioriza foco no envelope (interativo) ou no card expandido
      if (envelopeRef.current) {
        envelopeRef.current.focus();
      } else if (expandedCardRef.current) {
        expandedCardRef.current.focus();
      } else if (focusTargetRef.current) {
        focusTargetRef.current.focus();
      }
    }, 100);
  }

  useEffect(() => {
    if (!modalIsOpen) return;

    if (animationPhase === 'expanded') {
      setTimeout(() => {
        if (expandedCardRef.current) {
          expandedCardRef.current.focus();
        }
      }, 700);
    } else if (animationPhase === 'initial') {
      if (!isLoading) {
        setTimeout(() => {
          announceMessage('Clique no envelope para descobrir sua mensagem especial.');
        }, 300);
      }
    }
  }, [animationPhase, modalIsOpen, isLoading, announceMessage]);

  // Mensagens padrão
  const DEFAULT_RECOGNITION_MESSAGE =
    'E no trabalho, sua proximidade e relevância se fizeram presentes, apoiando resultados, fortalecendo a dinâmica colaborativa das equipes e contribuindo para o alcance dos objetivos do Banco.';

  async function fetchNewYearData() {
    try {
      setIsLoading(true);
      const data: QuizResponse[] = await getAnswersQuiz();

      // Verifica se tem dados da API
      if (data && data.length > 0) {
        const quizData = data[0];
        const hasQuestions = quizData.questions && quizData.questions.length > 0;
        const hasMessage = !!quizData.message?.trim();

        const mappedData: UserData = {
          cor: '',
          mood: '',
          superpoder: '',
          trilhaSonora: '',
          formaRecarregar: '',
          viagem: '',
          pequenaAlegria: '',
          atividadeFisica: '',
          simbolo: '',
          emoji: '',
          reconhecimento: '',
        };

        // Processa perguntas do quiz (se existirem)
        if (hasQuestions) {
          quizData.questions.forEach(item => {
            const questionText = item.question_text.toLowerCase();
            const labelText = extractLabelText(item.label);

            if (questionText.includes('cor representa')) {
              mappedData.cor = item.answer[0];
              // Define a imagem com base na cor escolhida
              const colorAnswer = item.answer[0];
              if (colorToImage[colorAnswer]) {
                setCardImage(colorToImage[colorAnswer]);
              }
            } else if (questionText.includes('mood')) {
              mappedData.mood = extractMoodText(item.label);
            } else if (questionText.includes('superpoder')) {
              mappedData.superpoder = labelText;
            } else if (questionText.includes('trilha sonora')) {
              mappedData.trilhaSonora = labelText;
            } else if (questionText.includes('recarrega')) {
              mappedData.formaRecarregar = labelText;
            } else if (questionText.includes('viagem')) {
              mappedData.viagem = labelText;
            } else if (questionText.includes('pequena alegria')) {
              mappedData.pequenaAlegria = labelText;
            } else if (questionText.includes('atividade física')) {
              mappedData.atividadeFisica = labelText;
            } else if (questionText.includes('símbolo')) {
              mappedData.simbolo = labelText;
            } else if (questionText.includes('emoji')) {
              mappedData.emoji = item.label; // Manter o emoji original
            }
          });
        }

        // Verifica se TODAS as perguntas necessárias para o texto personalizado foram respondidas
        // (exclui 'cor' e 'reconhecimento' que não fazem parte do texto personalizado)
        const hasAllQuizAnswers =
          !!mappedData.mood &&
          !!mappedData.superpoder &&
          !!mappedData.trilhaSonora &&
          !!mappedData.formaRecarregar &&
          !!mappedData.viagem &&
          !!mappedData.pequenaAlegria &&
          !!mappedData.atividadeFisica &&
          !!mappedData.simbolo &&
          !!mappedData.emoji;

        // Processa mensagem de reconhecimento (independente das questions)
        const RECOGNITION_PREFIX =
          'E no trabalho, proximidade e relevância estiveram presentes e seus colegas e líderes te reconheceram como uma pessoa';

        if (hasMessage && quizData.message) {
          let recognitionMessage = quizData.message.trim();
          // Se a mensagem não começar com o prefixo esperado, adiciona o prefixo
          if (!recognitionMessage.toLowerCase().startsWith('e no trabalho')) {
            recognitionMessage = `${RECOGNITION_PREFIX} ${recognitionMessage}`;
          }
          mappedData.reconhecimento = recognitionMessage;
        } else {
          mappedData.reconhecimento = DEFAULT_RECOGNITION_MESSAGE;
        }

        setUserData(mappedData);
        setHasQuizAnswers(hasAllQuizAnswers);
      } else {
        // Não tem dados da API - usa dados padrão
        setHasQuizAnswers(false);
      }
    } catch (error) {
      console.error('Erro ao buscar dados do quiz:', error);
      setHasQuizAnswers(false);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    const isEnvelopeModal = openModal === MODAL_ID_ENVELOPE;
    const isDirectModal = openModal === MODAL_ID_DIRECT;
    const shouldOpen = isEnvelopeModal || isDirectModal;

    setIsOpen(shouldOpen);
    setIsDirectMode(isDirectModal);

    if (shouldOpen) {
      // Se modo direto, vai direto para expanded; senão começa do initial
      setAnimationPhase(isDirectModal ? 'expanded' : 'initial');
      fetchNewYearData();
    }
  }, [openModal]);

  // Focus trap e ESC
  useEffect(() => {
    if (!modalIsOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        handleCloseModal();
      }
      if (e.key === 'Tab') {
        const focusableEls = focusTargetRef.current?.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (!focusableEls || focusableEls.length === 0) return;
        const firstEl = focusableEls[0];
        const lastEl = focusableEls[focusableEls.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === firstEl) {
            e.preventDefault();
            lastEl.focus();
          }
        } else {
          if (document.activeElement === lastEl) {
            e.preventDefault();
            firstEl.focus();
          }
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      // Retorna foco ao elemento que abriu
      previouslyFocusedElement.current?.focus();
    };
  }, [handleCloseModal, modalIsOpen]);

  function sendActionLog() {
    try {
      registerInteractionCampaign('me', 'new_year_2025_envelope', { interaction: 'open' }, null, null, false);
    } catch (error) {
      console.error('Error logging action:', error);
    }
  }

  // Gera e baixa uma imagem do card expandido (trata imagens S3 como fallback)
  async function handleDownload() {
    if (!expandedCardRef.current) return;

    try {
      // registrar interação (não bloqueante)
      try {
        registerInteractionCampaign('me', 'new_year_2025_envelope', { interaction: 'download' }, null, null, false);
      } catch (err) {
        console.error('Erro ao registrar download:', err);
      }

      const cardEl = expandedCardRef.current;

      // procurar avatar externo que começa com s3.amazonaws.com
      const imgEl = cardEl.querySelector('img[src^="https://s3.amazonaws.com"]') as HTMLImageElement;

      if (imgEl) {
        const originalSrc = imgEl.src;
        const regionalSrc = originalSrc.replace(
          /^https:\/\/s3\.amazonaws\.com\/([^/]+)\/(.+)$/,
          'https://$1.s3.amazonaws.com/$2',
        );

        try {
          const response = await fetch(regionalSrc, { mode: 'cors' });
          if (!response.ok) throw new Error(`Erro ao buscar imagem convertida: ${response.status}`);

          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);

          imgEl.src = blobUrl;

          const dataUrl = await domtoimage.toPng(cardEl);

          imgEl.src = originalSrc;
          URL.revokeObjectURL(blobUrl);

          const link = document.createElement('a');
          link.href = dataUrl;
          link.download = 'card-new-year.png';
          link.click();

          return;
        } catch (error) {
          console.warn('Falha ao usar URL convertida para avatar:', error);
          // continua para tentativa sem tratamento especial
        }
      }

      // fallback: gerar direto
      const dataUrl = await domtoimage.toPng(cardEl);
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = 'card-new-year.png';
      link.click();
    } catch (error) {
      console.error('Erro ao gerar imagem:', error);
    }
  }

  function generateFireworks() {
    const colors = ['#FFD700', '#FF6B6B', '#4ECDC4', '#A855F7', '#F97316', '#10B981', '#3B82F6', '#EC4899'];
    const newFireworks = [];
    for (let i = 0; i < 50; i++) {
      newFireworks.push({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        color: colors[Math.floor(Math.random() * colors.length)],
        delay: Math.random() * 3,
      });
    }
    setFireworks(newFireworks);
  }

  function handleEnvelopeClick() {
    if (animationPhase !== 'initial' || isLoading) return;

    // Apenas abre o envelope - a carta sobe via CSS transition (como no exemplo)
    setAnimationPhase('opening');

    // Aguarda a carta subir completamente (CSS transition de 0.5s + delay de 0.5s)
    // Depois faz a transição para o card expandido
    setTimeout(() => {
      // Fade out do envelope e carta
      if (envelopeRef.current) {
        gsap.to(envelopeRef.current, {
          opacity: 0,
          scale: 0.8,
          y: 50,
          duration: 0.4,
          ease: 'power2.in',
        });
      }

      if (letterRef.current) {
        gsap.to(letterRef.current, {
          opacity: 0,
          scale: 1.3,
          duration: 0.4,
          ease: 'power2.in',
        });
      }

      // Mostra o card expandido
      setTimeout(() => {
        setAnimationPhase('expanded');
        generateFireworks();

        setTimeout(() => {
          if (expandedCardRef.current) {
            gsap.fromTo(
              expandedCardRef.current,
              {
                scale: 0.5,
                opacity: 0,
                y: -30,
              },
              {
                scale: 1,
                opacity: 1,
                y: 0,
                duration: 0.6,
                ease: 'back.out(1.4)',
              },
            );
          }
        }, 50);
      }, 400);
    }, 1500); // Espera envelope abrir + carta subir
  }

  // Mensagem genérica quando não tem respostas do quiz
  const DEFAULT_NO_QUIZ_MESSAGE =
    'Em 2025, seu dia a dia foi marcado por diferentes ritmos e experiências que contribuíram para o equilíbrio, o bem-estar e o crescimento ao longo do ano, com momentos de energia, pausa e reconexão que ajudaram a manter foco, presença e disposição no cotidiano.';

  // Dados padrão caso não tenha resposta do quiz
  const defaultUserData: UserData = {
    cor: '3',
    mood: '',
    superpoder: '',
    trilhaSonora: '',
    formaRecarregar: '',
    viagem: '',
    pequenaAlegria: '',
    atividadeFisica: '',
    simbolo: '',
    emoji: '',
    reconhecimento:
      'E no trabalho, sua proximidade e relevância se fizeram presentes, apoiando resultados, fortalecendo a dinâmica colaborativa das equipes e contribuindo para o alcance dos objetivos do Banco.',
  };

  const displayData = userData || defaultUserData;

  // Mapeamento de cor para cor do texto strong
  const colorToStrongColor: Record<string, string> = {
    '1': '#465eff',
    '2': '#3333bd',
    '3': '#735cc6',
    '4': '#465eff',
    '5': '#ff6e91',
  };

  // Cor do strong baseada na cor do card
  const strongColor = colorToStrongColor[displayData.cor] || '#735cc6';

  return (
    <ReactModal
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={true}
      shouldReturnFocusAfterClose={false}
      isOpen={modalIsOpen}
      onAfterOpen={afterOpenModal}
      onRequestClose={handleCloseModal}
      contentLabel="Retrospectiva de Ano Novo 2025"
      portalClassName="relative z-[999]"
      overlayClassName="new-year-modal-overlay"
      className="new-year-modal-content"
      bodyOpenClassName="ReactModal__Body--open"
    >
      {/* Região aria-live oculta para leitores de tela */}
      {modalIsOpen && (
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            margin: -1,
            padding: 0,
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
            border: 0,
          }}
        >
          {liveMessage}
        </div>
      )}
      <div
        ref={focusTargetRef}
        tabIndex={-1}
        className="outline-none new-year-container"
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-year-title"
      >
        {/* Fogos de artifício de fundo */}
        <div className="fireworks-container" aria-hidden="true">
          {fireworks.map(fw => (
            <div
              key={fw.id}
              className="firework"
              style={{
                left: `${fw.x}%`,
                top: `${fw.y}%`,
                backgroundColor: fw.color,
                animationDelay: `${fw.delay}s`,
                boxShadow: `0 0 6px ${fw.color}, 0 0 12px ${fw.color}`,
              }}
            />
          ))}
        </div>

        {/* Botão fechar */}
        {animationPhase === 'expanded' && (
          <Button
            color="primary"
            className="new-year-close-btn"
            onClick={handleCloseModal}
            type="button"
            aria-label="Fechar modal"
            ref={closeBtnRef}
          >
            <IoIosClose color="white" size={32} />
          </Button>
        )}

        {/* ===== FASE INICIAL E ABERTURA: Envelope com carta dentro ===== */}
        {animationPhase !== 'expanded' && (
          <div className="envelope-section">
            <h1 id="new-year-title" className="new-year-title">
              <span className="title-year">2025</span>
              <span className="title-text">Sua Retrospectiva</span>
            </h1>

            {animationPhase === 'initial' && (
              <p className="new-year-instruction" id="new-year-desc" aria-hidden="true">
                {isLoading ? 'Preparando sua mensagem...' : 'Clique no envelope para descobrir sua mensagem especial ✨'}
              </p>
            )}

            {/* Envelope com carta DENTRO (como no exemplo) */}
            <div
              ref={envelopeRef}
              className={`envelope-wrapper ${animationPhase === 'opening' ? 'opened' : ''} ${isLoading ? 'loading' : ''}`}
              onClick={handleEnvelopeClick}
              onKeyDown={e => e.key === 'Enter' && handleEnvelopeClick()}
              tabIndex={0}
              role="button"
              aria-label="Abrir envelope com sua retrospectiva"
            >
              {/* Partículas flutuantes */}
              <div className="envelope-particles" aria-hidden="true">
                <div className="envelope-particle"></div>
                <div className="envelope-particle"></div>
                <div className="envelope-particle"></div>
                <div className="envelope-particle"></div>
                <div className="envelope-particle"></div>
                <div className="envelope-particle"></div>
              </div>

              <div className="lid one"></div>
              <div className="lid two"></div>
              <div className="envelope"></div>

              {/* Lacre do envelope */}
              <div className="envelope-seal">
                <img src={lacreEnvelope} alt="Lacre" className="seal-image" />
              </div>

              {/* Carta DENTRO do envelope - sobe quando abre */}
              <div ref={letterRef} className="letter" style={{ backgroundImage: `url(${cardImage})` }}>
                <div className="letter-content">
                  <div className="letter-avatar">
                    <UserAvatar data={{ ...user, ...user?.extra }} responsive={true} />
                  </div>
                  <p className="letter-greeting" style={{ color: strongColor }}>
                    Olá, {userInfo?.nome_guerra?.split(' ')[0] || 'Colega'}!
                  </p>
                  <p className="letter-subtitle">Sua retrospectiva 2025</p>
                  <div className="letter-sparkles">✨ 🎉 ✨</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== FASE EXPANDIDA: Card Grande ===== */}
        {animationPhase === 'expanded' && (
          <div className="expanded-section">
            {/* Card principal */}
            <div
              ref={expandedCardRef}
              className="expanded-card outline-none"
              style={{ backgroundImage: `url(${cardImage})` }}
              tabIndex={-1}
            >
              <div className="user-avatar-container" aria-hidden="true">
                <UserAvatar data={{ ...user, ...user?.extra }} responsive={true} />
              </div>

              <div className="expanded-card-content">
                <h2 className="card-greeting" style={{ color: strongColor }}>
                  Olá, {userInfo?.nome_guerra?.split(' ')[0] || 'Colega'}! 🎉
                </h2>

                <div className="card-text">
                  {hasQuizAnswers ? (
                    <>
                      <p>
                        Em 2025, seu dia a dia ganhou leveza com seu jeito de começar em{' '}
                        <strong style={{ color: strongColor }}>{displayData.mood}</strong>. Seu superpoder,{' '}
                        <strong style={{ color: strongColor }}>{displayData.superpoder}</strong>, marcou suas entregas, enquanto{' '}
                        <strong style={{ color: strongColor }}>{displayData.trilhaSonora}</strong> acompanhou sua rotina.
                      </p>

                      <p>
                        Você recarregou energia <strong style={{ color: strongColor }}>{displayData.formaRecarregar}</strong>,
                        celebrou <strong style={{ color: strongColor }}>{displayData.pequenaAlegria}</strong> e viveu viagens do
                        tipo <strong style={{ color: strongColor }}>{displayData.viagem}</strong>.
                      </p>

                      <p>
                        No seu ritmo, <strong style={{ color: strongColor }}>{displayData.atividadeFisica}</strong> ajudou a
                        equilibrar a rotina, com o simbolismo de{' '}
                        <strong style={{ color: strongColor }}>{displayData.simbolo}</strong> e a vibe de{' '}
                        <strong style={{ color: strongColor }}>{displayData.emoji}</strong>.
                      </p>
                    </>
                  ) : (
                    <p>{DEFAULT_NO_QUIZ_MESSAGE}</p>
                  )}

                  <p className="recognition-text">{highlightRecognitionText(displayData.reconhecimento, strongColor)}</p>

                  <p className="card-footer-message">
                    <strong style={{ color: strongColor }}>Feliz 2026!</strong> Seguimos{' '}
                    <strong style={{ color: strongColor }}>JUNTOS</strong> rumo <br /> a uma vida de oportunidades.
                  </p>
                </div>
              </div>
            </div>

            {/* Área de ações */}
            <div className="expanded-actions">
              <Button color="primary" onClick={handleDownload} type="button" className="download-btn">
                Baixar imagem
              </Button>

              <p className="expanded-disclaimer">
                Este conteúdo foi gerado por inteligência artificial com base nas suas respostas.
              </p>
            </div>
          </div>
        )}

        {/* Confetes quando expande
        {animationPhase === 'expanded' && (
          <div className="confetti-celebration">
            {Array.from({ length: 80 }).map((_, i) => (
              <div
                key={i}
                className="confetti-piece"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`,
                  animationDuration: `${3 + Math.random() * 2}s`,
                  backgroundColor: ['#FFD700', '#FF6B6B', '#4ECDC4', '#A855F7', '#F97316', '#10B981'][
                    Math.floor(Math.random() * 6)
                  ],
                }}
              />
            ))}
          </div>
        )} */}
      </div>
    </ReactModal>
  );
}
